import React, { useState, useEffect } from 'react'
import { X, Building2, Instagram, Settings, BarChart3, Users, Radar, Merge
} from 'lucide-react'
import { Card, Button } from '../ui'
import {
  organizationService,
  Organization,
  CreateOrganizationRequest,
  UpdateOrganizationRequest,
  SubscriptionTier
} from '../../services/organizationService'
import OrganizationAccounts from './OrganizationAccounts'
import OrganizationFeatures from './OrganizationFeatures'
import OrganizationLimits from './OrganizationLimits'
import OrganizationUsers from './OrganizationUsers'
import OrganizationBrandScout from './OrganizationBrandScout'
import BrandCurationPanel from '../scout/BrandCurationPanel'
import { useAuthStore } from '../../stores/authStore'
import { isSystemAdmin, hasSupportRole } from '../../utils/permissions'

interface OrganizationTabbedDrawerProps {
  isOpen: boolean
  onClose: () => void
  organization?: Organization | null
  isCreating: boolean
  onSaved: () => void
}

type TabType = 'details' | 'accounts' | 'features' | 'limits' | 'users' | 'brand-scout' | 'brand-curation'

const OrganizationTabbedDrawer: React.FC<OrganizationTabbedDrawerProps> = ({
  isOpen,
  onClose,
  organization,
  isCreating,
  onSaved
}) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [subscriptionTiers, setSubscriptionTiers] = useState<
    SubscriptionTier[]
  >([])
  const [tiersLoading, setTiersLoading] = useState(false)
  const [fullOrganization, setFullOrganization] = useState<Organization | null>(
    null
  )
  const [loadingFullOrg, setLoadingFullOrg] = useState(false)
  const [activeTab, setActiveTab] = useState<TabType>('details')
  const { user } = useAuthStore()
  // Brand Scout admin tooling — visible to system admins and platform support staff
  // (backend endpoints are gated on access:support, which both roles carry).
  const canSeeBrandScout = isSystemAdmin(user) || hasSupportRole(user)

  const [formData, setFormData] = useState({
    organizationName: '',
    subscriptionTierId: 1,
    subscriptionStatus: 'active' as
      | 'active'
      | 'trial'
      | 'expired'
      | 'cancelled',
    subscriptionStartsAt: '',
    subscriptionExpiresAt: '',
    billingCustomerId: '',
    isActive: true
  })

  // Reset tab when opening/closing or switching organizations
  useEffect(() => {
    if (isOpen) {
      setActiveTab('details')
      setError(null)
    }
  }, [isOpen, organization])

  // Load subscription tiers when drawer opens
  useEffect(() => {
    const loadSubscriptionTiers = async () => {
      if (isOpen && subscriptionTiers.length === 0) {
        setTiersLoading(true)
        try {
          const response = await organizationService.getSubscriptionTiers()
          if (response.success) {
            setSubscriptionTiers(response.data)
          }
        } catch (error) {
          console.error('Error loading subscription tiers:', error)
        } finally {
          setTiersLoading(false)
        }
      }
    }

    loadSubscriptionTiers()
  }, [isOpen, subscriptionTiers.length])

  // Load full organization details when editing
  useEffect(() => {
    const loadFullOrganization = async () => {
      if (isOpen && !isCreating && organization?.id && !fullOrganization) {
        setLoadingFullOrg(true)
        try {
          const response = await organizationService.getOrganization(
            organization.id
          )
          if (response.success) {
            setFullOrganization(response.data)
            // Update form data with full organization details
            const org = response.data
            setFormData({
              organizationName: org.organizationName || '',
              subscriptionTierId: org.subscriptionTierId || 1,
              subscriptionStatus:
                (org.subscriptionStatus as
                  | 'active'
                  | 'trial'
                  | 'expired'
                  | 'cancelled') || 'active',
              subscriptionStartsAt: org.subscriptionStartsAt
                ? new Date(org.subscriptionStartsAt).toISOString().split('T')[0]
                : '',
              subscriptionExpiresAt: org.subscriptionExpiresAt
                ? new Date(org.subscriptionExpiresAt)
                    .toISOString()
                    .split('T')[0]
                : '',
              billingCustomerId: org.billingCustomerId || '',
              isActive: org.isActive !== undefined ? org.isActive : true
            })
          }
        } catch (error) {
          console.error('Error loading full organization:', error)
        } finally {
          setLoadingFullOrg(false)
        }
      }
    }

    loadFullOrganization()
  }, [isOpen, isCreating, organization?.id, fullOrganization])

  // Reset form and organization data when creating or organization changes
  useEffect(() => {
    if (isOpen) {
      // Always reset fullOrganization when organization changes to force reload
      setFullOrganization(null)

      if (isCreating) {
        // Only reset form data when creating new organization
        setFormData({
          organizationName: '',
          subscriptionTierId: 1,
          subscriptionStatus: 'active',
          subscriptionStartsAt: '',
          subscriptionExpiresAt: '',
          billingCustomerId: '',
          isActive: true
        })
      } else {
        // When editing, initialize with the basic organization data until full data loads
        setFormData({
          organizationName: organization?.organizationName || '',
          subscriptionTierId: organization?.subscriptionTierId || 1,
          subscriptionStatus:
            (organization?.subscriptionStatus as
              | 'active'
              | 'trial'
              | 'expired'
              | 'cancelled') || 'active',
          subscriptionStartsAt: organization?.subscriptionStartsAt
            ? new Date(organization.subscriptionStartsAt)
                .toISOString()
                .split('T')[0]
            : '',
          subscriptionExpiresAt: organization?.subscriptionExpiresAt
            ? new Date(organization.subscriptionExpiresAt)
                .toISOString()
                .split('T')[0]
            : '',
          billingCustomerId: organization?.billingCustomerId || '',
          isActive:
            organization?.isActive !== undefined ? organization.isActive : true
        })
      }
    }
  }, [isOpen, isCreating, organization])

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.organizationName.trim()) {
      setError('Organization name is required')
      return
    }

    try {
      setLoading(true)
      setError(null)

      if (isCreating) {
        const createData: CreateOrganizationRequest = {
          organizationName: formData.organizationName.trim()
        }

        const response = await organizationService.createOrganization(
          createData
        )

        if (response.success) {
          onSaved()
        } else {
          throw new Error('Failed to create organization')
        }
      } else if (organization) {
        const updateData: UpdateOrganizationRequest = {
          organizationName: formData.organizationName.trim(),
          subscriptionTierId: formData.subscriptionTierId,
          subscriptionStatus: formData.subscriptionStatus,
          subscriptionStartsAt: formData.subscriptionStartsAt || undefined,
          subscriptionExpiresAt: formData.subscriptionExpiresAt || undefined,
          billingCustomerId: formData.billingCustomerId.trim() || undefined,
          isActive: formData.isActive
        }

        const response = await organizationService.updateOrganization(
          organization.id,
          updateData
        )

        if (response.success) {
          onSaved()
        } else {
          throw new Error(
            response.error?.message || 'Failed to update organization'
          )
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  const tabs = [
    { id: 'details' as TabType, name: 'Details', icon: Building2 },
    { id: 'accounts' as TabType, name: 'Accounts', icon: Instagram },
    { id: 'features' as TabType, name: 'Features', icon: Settings },
    { id: 'limits' as TabType, name: 'Limits', icon: BarChart3 },
    { id: 'users' as TabType, name: 'Users', icon: Users },
    // Brand Scout admin tooling — system admins only.
    { id: 'brand-scout' as TabType, name: 'Brand Scout', icon: Radar },
    // Brand curation: merge name variants, rename labels, undo either. Same gate as Brand
    // Scout, since it acts on the same data on a customer's behalf.
    { id: 'brand-curation' as TabType, name: 'Brand Curation', icon: Merge }
  ]

  // Don't show non-details tabs when creating; gate Brand Scout to system admins + support.
  const availableTabs = (
    isCreating ? tabs.filter((tab) => tab.id === 'details') : tabs
  ).filter((tab) =>
    (tab.id !== 'brand-scout' && tab.id !== 'brand-curation') || canSeeBrandScout
  )

  if (!isOpen) return null

  return (
    <div className='fixed inset-0 z-50 overflow-hidden'>
      <div
        className='absolute inset-0 bg-black bg-opacity-50'
        onClick={onClose}
      />

      <div className='absolute right-0 top-0 h-full w-full max-w-4xl bg-white shadow-xl'>
        <div className='flex h-full flex-col'>
          {/* Header */}
          <div className='flex items-center justify-between px-6 py-4 border-b border-gray-200'>
            <div className='flex items-center space-x-3'>
              <div className='p-2 bg-blue-100 rounded-lg'>
                <Building2 className='h-5 w-5 text-blue-600' />
              </div>
              <div>
                <h2 className='text-lg font-semibold text-gray-900'>
                  {isCreating
                    ? 'Create Organization'
                    : fullOrganization?.organizationName ||
                      organization?.organizationName}
                </h2>
                <p className='text-sm text-gray-600'>
                  {isCreating
                    ? 'Create a new organization'
                    : 'Manage organization details and accounts'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className='p-2 hover:bg-gray-100 rounded-lg transition-colors'
            >
              <X className='h-5 w-5 text-gray-400' />
            </button>
          </div>

          {/* Tabs */}
          <div className='border-b border-gray-200'>
            <nav className='flex space-x-8 px-6'>
              {availableTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-avi-primary text-avi-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className='flex items-center space-x-2'>
                    <tab.icon className='h-4 w-4' />
                    <span>{tab.name}</span>
                  </div>
                </button>
              ))}
            </nav>
          </div>

          {/* Content */}
          <div className='flex-1 overflow-y-auto'>
            {activeTab === 'details' && (
              <div className='p-6'>
                <form onSubmit={handleSubmit} className='space-y-6'>
                  {/* Error Message */}
                  {error && (
                    <div className='bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg'>
                      {error}
                    </div>
                  )}

                  {/* Organization Details */}
                  <Card variant='outlined' className='p-4'>
                    <h3 className='text-sm font-medium text-gray-900 mb-4'>
                      Organization Details
                    </h3>

                    <div className='space-y-4'>
                      <div>
                        <label className='block text-sm font-medium text-gray-700 mb-2'>
                          Organization Name *
                        </label>
                        <input
                          type='text'
                          value={formData.organizationName}
                          onChange={(e) =>
                            handleInputChange(
                              'organizationName',
                              e.target.value
                            )
                          }
                          className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-avi-primary focus:border-transparent'
                          placeholder='Enter organization name'
                          required
                        />
                      </div>

                      {!isCreating && (
                        <>
                          <div className='grid grid-cols-2 gap-4'>
                            <div>
                              <label className='block text-sm font-medium text-gray-700 mb-2'>
                                Organization ID
                              </label>
                              <input
                                type='text'
                                value={organization?.id || ''}
                                className='w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 font-mono text-sm'
                                readOnly
                              />
                            </div>
                            <div>
                              <label className='block text-sm font-medium text-gray-700 mb-2'>
                                Customer ID
                              </label>
                              <input
                                type='text'
                                value={organization?.customerId || ''}
                                className='w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 font-mono text-sm'
                                readOnly
                              />
                            </div>
                          </div>
                        </>
                      )}

                      <div className='flex items-center space-x-2'>
                        <input
                          type='checkbox'
                          id='isActive'
                          checked={formData.isActive}
                          onChange={(e) =>
                            handleInputChange('isActive', e.target.checked)
                          }
                          className='h-4 w-4 text-avi-primary focus:ring-avi-primary border-gray-300 rounded'
                        />
                        <label
                          htmlFor='isActive'
                          className='text-sm text-gray-700'
                        >
                          Organization is active
                        </label>
                      </div>
                    </div>
                  </Card>

                  {/* Subscription Details */}
                  <Card variant='outlined' className='p-4'>
                    <h3 className='text-sm font-medium text-gray-900 mb-4'>
                      Subscription Details
                    </h3>

                    <div className='space-y-4'>
                      <div className='grid grid-cols-2 gap-4'>
                        <div>
                          <label className='block text-sm font-medium text-gray-700 mb-2'>
                            Subscription Tier
                          </label>
                          <select
                            value={formData.subscriptionTierId}
                            onChange={(e) =>
                              handleInputChange(
                                'subscriptionTierId',
                                parseInt(e.target.value)
                              )
                            }
                            className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-avi-primary focus:border-transparent'
                            disabled={tiersLoading}
                          >
                            {tiersLoading ? (
                              <option>Loading tiers...</option>
                            ) : (
                              subscriptionTiers.map((tier) => (
                                <option key={tier.id} value={tier.id}>
                                  {tier.tierName} - $
                                  {tier.pricing.monthly_price}/month
                                </option>
                              ))
                            )}
                          </select>
                        </div>

                        <div>
                          <label className='block text-sm font-medium text-gray-700 mb-2'>
                            Status
                          </label>
                          <select
                            value={formData.subscriptionStatus}
                            onChange={(e) =>
                              handleInputChange(
                                'subscriptionStatus',
                                e.target.value
                              )
                            }
                            className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-avi-primary focus:border-transparent'
                          >
                            <option value='active'>Active</option>
                            <option value='trial'>Trial</option>
                            <option value='expired'>Expired</option>
                            <option value='cancelled'>Cancelled</option>
                          </select>
                        </div>
                      </div>

                      <div className='grid grid-cols-2 gap-4'>
                        <div>
                          <label className='block text-sm font-medium text-gray-700 mb-2'>
                            Subscription Starts
                          </label>
                          <input
                            type='date'
                            value={formData.subscriptionStartsAt}
                            onChange={(e) =>
                              handleInputChange(
                                'subscriptionStartsAt',
                                e.target.value
                              )
                            }
                            className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-avi-primary focus:border-transparent'
                          />
                        </div>

                        <div>
                          <label className='block text-sm font-medium text-gray-700 mb-2'>
                            Subscription Expires
                          </label>
                          <input
                            type='date'
                            value={formData.subscriptionExpiresAt}
                            onChange={(e) =>
                              handleInputChange(
                                'subscriptionExpiresAt',
                                e.target.value
                              )
                            }
                            className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-avi-primary focus:border-transparent'
                          />
                        </div>
                      </div>

                      <div>
                        <label className='block text-sm font-medium text-gray-700 mb-2'>
                          Billing Customer ID
                        </label>
                        <input
                          type='text'
                          value={formData.billingCustomerId}
                          onChange={(e) =>
                            handleInputChange(
                              'billingCustomerId',
                              e.target.value
                            )
                          }
                          className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-avi-primary focus:border-transparent'
                          placeholder='Enter billing customer ID (optional)'
                        />
                      </div>
                    </div>
                  </Card>
                </form>
              </div>
            )}

            {activeTab === 'accounts' && !isCreating && fullOrganization && (
              <div className='p-6'>
                <OrganizationAccounts
                  organization={fullOrganization}
                  onRefresh={() => {
                    // Optionally refresh organization data
                    onSaved()
                  }}
                />
              </div>
            )}

            {activeTab === 'features' && !isCreating && fullOrganization && (
              <div className='p-6'>
                <OrganizationFeatures
                  organization={fullOrganization}
                  onRefresh={() => {
                    // Optionally refresh organization data
                    onSaved()
                  }}
                />
              </div>
            )}

            {activeTab === 'limits' && !isCreating && fullOrganization && (
              <div className='p-6'>
                <OrganizationLimits
                  organization={fullOrganization}
                  onRefresh={() => {
                    // Optionally refresh organization data
                    onSaved()
                  }}
                />
              </div>
            )}

            {activeTab === 'users' && !isCreating && fullOrganization && (
              <div className='p-6'>
                <OrganizationUsers organization={fullOrganization} />
              </div>
            )}

            {activeTab === 'brand-scout' &&
              !isCreating &&
              fullOrganization &&
              canSeeBrandScout && (
                <div className='p-6'>
                  <OrganizationBrandScout organization={fullOrganization} />
                </div>
              )}

            {activeTab === 'brand-curation' &&
              !isCreating &&
              fullOrganization &&
              canSeeBrandScout && (
                <div className='p-6'>
                  <BrandCurationPanel
                    customerId={fullOrganization.customerId}
                    context='support'
                  />
                </div>
              )}
          </div>

          {/* Footer - only show on details tab */}
          {activeTab === 'details' && (
            <div className='flex items-center justify-end space-x-3 px-6 py-4 border-t border-gray-200 bg-gray-50'>
              <Button variant='outline' onClick={onClose}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={loading || loadingFullOrg}
                className='min-w-[120px]'
              >
                {loading ? (
                  'Saving...'
                ) : (
                  <>{isCreating ? 'Create Organization' : 'Save Changes'}</>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default OrganizationTabbedDrawer
